import { type AxiosResponse } from 'axios'
import { type FormTypes } from '../features/CoursesAndEnrollment/SyncSectionModal/SurveyDatesPanel'
import { type ISurveyDates } from '../models/AutoImports'
import api from '../utils/api'
import { notification } from '../components/Notifications'

export const submitSurveyDates = async (data: { payload: Array<Partial<FormTypes>>, slotId: string }): Promise<string | any> => {
  const { payload, slotId } = data
  try {
    const response = await api.post(`/api/slots/${slotId}/default_date_settings/`, payload)
    return { data: response.data, status: response.status }
  } catch (error: any) {
    throw new Error(error.response?.data ?? 'Error submitting survey dates')
  }
}

export const getSurveyDates = async (slotId: string): Promise<ISurveyDates[]> => {
  try {
    const response = await api.get(`/api/slots/${slotId}/default_date_settings/`)
    return response.data
  } catch (error: any) {
    throw new Error(error.response?.data ?? 'Error getting survey dates')
  }
}

export const getSectionFields = async (): Promise<string[]> => {
  try {
    const response = await api.get('/api/slots/section_fields/')
    return response.data
  } catch (error: any) {
    throw new Error(error.response?.data ?? 'Error getting section fields')
  }
}

export const submitAutoImport = async ({ slotId, data }: { slotId: string, data: any }): Promise<AxiosResponse> => {
  return await api.post(`/api/slots/${slotId}/auto_import/`, data)
}

export const updateSlot = async (data: any): Promise<string | any> => {
  try {
    const response = await api.patch(`/api/slots/${data.slotId}`, data.payload)
    return response.data
  } catch (error: any) {
    return error
  }
}

export const getSlotById = async (slotId: string): Promise<string | any> => {
  try {
    const response = await api.get(`/api/slots/${slotId}`)
    return response.data
  } catch (error: any) {
    return error
  }
}

export const surveyPreview = async (slotId: string, sectionId: string): Promise<string | any> => {
  try {
    const response = await api.get(`/api/slots/${slotId}/sections/${sectionId}/preview/`)
    return response.data
  } catch (error: any) {
    notification(error.message, { variant: 'error' })
    throw new Error(error.message ?? 'Error getting survey')
  }
}

export const getAllInstructorQuestions = async (slotId: string, sectionId: string, params?: any): Promise<AxiosResponse> => {
  return await api.get(`/api/slots/${slotId}/sections/${sectionId}/template/`, { params })
}

export const postAllInstructorQuestions = async (
  slotId: string,
  sectionId: string,
  payload: any,
  params?: any
): Promise<AxiosResponse> => {
  return await api.post(
    `/api/slots/${slotId}/sections/${sectionId}/template/`,
    payload,
    { params }
  )
}

export const patchAllInstructorQuestions = async (
  slotId: string,
  sectionId: string,
  payload: any,
  params?: any
): Promise<AxiosResponse> => {
  return await api.patch(
    `/api/slots/${slotId}/sections/${sectionId}/template/`,
    payload,
    { params }
  )
}

export const getAllAdminQuestions = async (slotId: string, divisionId: string, params?: any): Promise<AxiosResponse> => {
  return await api.get(`/api/slots/${slotId}/divisions/${divisionId}/template/`, { params })
}

export const postAllAdminQuestions = async (
  slotId: string,
  divisionId: string,
  payload: any,
  params?: any
): Promise<AxiosResponse> => {
  return await api.post(
    `/api/slots/${slotId}/divisions/${divisionId}/template/`,
    payload,
    { params }
  )
}

export const patchAllAdminQuestions = async (
  slotId: string,
  divisionId: string,
  payload: any,
  params?: any
): Promise<AxiosResponse> => {
  return await api.patch(
    `/api/slots/${slotId}/divisions/${divisionId}/template/`,
    payload,
    { params }
  )
}

export default {
  submitSurveyDates,
  getSurveyDates,
  getSectionFields,
  submitAutoImport,
  updateSlot,
  getSlotById,
  surveyPreview,
  getAllInstructorQuestions,
  postAllInstructorQuestions,
  patchAllInstructorQuestions,
  getAllAdminQuestions,
  postAllAdminQuestions,
  patchAllAdminQuestions
}
